import { useMemo, useState } from 'react';
import {
  AssetPortfolio,
  AssetPortfolioComponent,
  AssetPortfolioComponentConditionType,
} from '../../type/AssetPortfolio.type';
import {
  ASSET_TYPE_OPTION_LIST,
  AssetItem,
  AssetType,
  LIABILITY_TYPE_OPTION_LIST,
  LiabilityItem,
  LiabilityType,
  OWNER_TYPE_OPTION_LIST,
  REPORT_ITEM_TYPE,
  REPORT_ITEM_TYPE_OPTION_LIST,
  ReportItemType,
  ReportOwnerType,
} from '../../../assetReport/type/AssetReport.type';
import EditAssetPortfolioComponentConditionList from './EditAssetPortfolioComponentConditionList';
import { useAppStateCurrentAssetReportStore } from '../../../../common/store/AppStateStore.zustand';
import { getAssetPortfolioComponentItemList } from '../../util/AssetPortfolioUtils';
import { ASSET_REPORT_LABEL } from '../../../../common/comstant/LabelConstants';

export interface AddAssetPortfolioComponentModalData {
  isOpen: boolean;
  assetPortfolioComponent: AssetPortfolioComponent;
  onClose: (assetPortfolioComponent?: AssetPortfolioComponent) => void;
}

const AddAssetPortfolioComponentModal = ({
  isOpen,
  assetPortfolioComponent,
  onClose,
}: AddAssetPortfolioComponentModalData) => {
  const { currentAssetReport } = useAppStateCurrentAssetReportStore();
  const [newAssetPortfolioComponent, setNewAssetPortfolioComponent] =
    useState<AssetPortfolioComponent>(assetPortfolioComponent);

  const editYn = useMemo(() => !!assetPortfolioComponent.name, [assetPortfolioComponent]);

  const filteredReportItemList = useMemo(
    () =>
      getAssetPortfolioComponentItemList(
        newAssetPortfolioComponent,
        currentAssetReport ? currentAssetReport.reportItemList : [],
      ),
    [currentAssetReport, newAssetPortfolioComponent],
  );

  const validInputYn = useMemo(
    () =>
      newAssetPortfolioComponent.name &&
      newAssetPortfolioComponent.targetRate > 0 &&
      newAssetPortfolioComponent.conditionList.length > 0,
    [newAssetPortfolioComponent],
  );

  const handleCloseClick = () => {
    onClose();
  };

  const handleAddClick = () => {
    validInputYn && onClose(newAssetPortfolioComponent);
  };

  const handleComponentChange = (target: string, value: any) => {
    setNewAssetPortfolioComponent({
      ...newAssetPortfolioComponent,
      [target]: value,
    });
  };

  const handleComponentConditionChange = (index: number, target: string, value: any) => {
    newAssetPortfolioComponent.conditionList[index] = {
      ...newAssetPortfolioComponent.conditionList[index],
      [target]: value,
    };
    setNewAssetPortfolioComponent({
      ...newAssetPortfolioComponent,
      conditionList: [...newAssetPortfolioComponent.conditionList],
    });
  };

  const handleAddComponentConditionClick = (includeYn: boolean) => {
    setNewAssetPortfolioComponent({
      ...newAssetPortfolioComponent,
      conditionList: [
        ...newAssetPortfolioComponent.conditionList,
        {
          id: '',
          includeYn: includeYn,
          ownerTypeCondition: undefined,
          type: undefined,
          nameCondition: '',
          reportItemTypeCondition: undefined,
          assetTypeCondition: undefined,
          liabilityTypeCondition: undefined,
        },
      ],
    });
  };

  const handleDeleteComponentConditionClick = (index: number) => {
    setNewAssetPortfolioComponent({
      ...newAssetPortfolioComponent,
      conditionList: [
        ...newAssetPortfolioComponent.conditionList.filter((condition, conditionIndex) => conditionIndex !== index),
      ],
    });
  };

  return (
    <div className="fixed w-full md:max-w-[800px] bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-8 py-6 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">구성요소 {editYn ? '수정' : '추가'}</h2>
            <button onClick={handleCloseClick} className="text-gray-400 hover:text-gray-600 transition-colors">
              <i className="fas fa-times text-lg"></i>
            </button>
          </div>
        </div>
        <div className="px-8 py-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">이름</label>
              <div className="relative">
                <input
                  type="text"
                  value={newAssetPortfolioComponent.name || ''}
                  onChange={(e) => handleComponentChange('name', e.target.value)}
                  className="w-full pl-4 pr-4 py-3 bg-gray-50 border-none rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-sm"
                  placeholder="예: 국내 주식"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">목표 비율 (%)</label>
              <div className="relative">
                <input
                  type="number"
                  value={newAssetPortfolioComponent.targetRate || ''}
                  onChange={(e) => handleComponentChange('targetRate', Number(e.target.value))}
                  className="w-full pl-4 pr-4 py-3 bg-gray-50 border-none rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-sm"
                  placeholder="40"
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center py-2">
                <label className="text-sm font-semibold text-gray-700 mb-2">포함 조건</label>
                <button
                  onClick={() => handleAddComponentConditionClick(true)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg shadow-sm transition-colors !rounded-button whitespace-nowrap bg-indigo-500 hover:bg-indigo-600 text-white`}
                >
                  조건 추가
                </button>
              </div>
              <EditAssetPortfolioComponentConditionList
                includeYn={true}
                componentConditionList={newAssetPortfolioComponent.conditionList}
                handleComponentConditionChange={handleComponentConditionChange}
                handleDeleteComponentConditionClick={handleDeleteComponentConditionClick}
              />
            </div>

            <div>
              <div className="flex justify-between items-center py-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">제외 조건</label>
                <button
                  onClick={() => handleAddComponentConditionClick(false)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg shadow-sm transition-colors !rounded-button whitespace-nowrap bg-indigo-500 hover:bg-indigo-600 text-white`}
                >
                  조건 추가
                </button>
              </div>
              <EditAssetPortfolioComponentConditionList
                includeYn={false}
                componentConditionList={newAssetPortfolioComponent.conditionList}
                handleComponentConditionChange={handleComponentConditionChange}
                handleDeleteComponentConditionClick={handleDeleteComponentConditionClick}
              />
            </div>
          </div>
          <div className="mt-6">
            <div
              className="flex items-center justify-between mb-3"
              onClick={(e) => {
                const target = e.currentTarget.nextElementSibling as HTMLElement;
                if (target) {
                  target.style.display = target.style.display === 'none' ? 'grid' : 'none';
                }
                const icon = e.currentTarget.querySelector('i.fa-chevron-down');
                if (icon) {
                  icon.classList.toggle('rotate-180');
                }
              }}
            >
              <h4 className="text-sm font-medium text-gray-700">
                포함 자산 목록
                <span className="ml-1 text-xs text-gray-500">(총 {filteredReportItemList.length}개의 자산)</span>
              </h4>
              <button className="flex items-center justify-between text-sm font-medium text-gray-700  hover:text-gray-900">
                <i className="fas fa-chevron-down transition-transform duration-200"></i>
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {filteredReportItemList.map((reportItem) => (
                <div className="bg-white rounded p-3 shadow-sm shadow-md">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{reportItem.name}</span>
                  </div>
                  <div className="flex items-center text-xs text-gray-500">
                    <i className="fas fa-user mr-1"></i>
                    <span>{ASSET_REPORT_LABEL[reportItem.owner]}</span>
                    <span className="mx-2">•</span>
                    <span>{ASSET_REPORT_LABEL[reportItem.type]}</span>
                    <span className="mx-2">•</span>
                    <span>
                      {
                        ASSET_REPORT_LABEL[
                          reportItem.type === REPORT_ITEM_TYPE.ASSET
                            ? (reportItem as AssetItem).assetType
                            : (reportItem as LiabilityItem).liabilityType
                        ]
                      }
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="px-8 py-6 border-t border-gray-100 bg-gray-50">
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleCloseClick}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 hover:text-gray-900 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors !rounded-button whitespace-nowrap shadow-sm"
            >
              취소
            </button>
            <button
              onClick={handleAddClick}
              disabled={!validInputYn}
              className={`px-6 py-2.5 text-sm font-medium rounded-lg shadow-sm transition-colors !rounded-button whitespace-nowrap
              ${
                !validInputYn
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-indigo-500 hover:bg-indigo-600 text-white'
              }`}
            >
              구성요소 {editYn ? '수정' : '추가'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAssetPortfolioComponentModal;
